// src/composables/useAnalyticsEvents.js
import { analyticsEvents } from '@/services/analytics'
import { useDataStore } from '@/stores/data'
import { useAppStore } from '@/stores/app'

export function useAnalyticsEvents() {
	const defaultEvents = () => {
		const appStore = useAppStore()
		const dataStore = useDataStore()
		const events = {
			app_version: appStore.version,
			app_version_date: appStore.version_date,
			platform: dataStore.platform || 'web',
			timestamp: Date.now(),
		}
		if (dataStore.appInstallationId) events.app_installation_id = dataStore.appInstallationId
		if (appStore.applicationId) events.app_id = appStore.applicationId
		if (dataStore.respondentAltId) events.respondent_alt_id = dataStore.respondentAltId
		if (dataStore.respondentShortId) events.respondent_short_id = dataStore.respondentShortId
		return events
	}

	return {
		setUserProperties: () => {
			const appStore = useAppStore()
			const dataStore = useDataStore()
			analyticsEvents.setUserProperties(appStore, dataStore)
		},

		// All Vue app events - use dataStore.respondentShortId
		trackAppStart: () => {
			analyticsEvents.appStart(defaultEvents())
		},

		trackStartClick: () => {
			analyticsEvents.startClicked(defaultEvents()) // Fixed method name
		},

		trackConsentApproved: () => {
			analyticsEvents.consentApproved(defaultEvents())
		},

		trackPermissionsApproved: (permissions = []) => {
			const events = defaultEvents()
			if (permissions.length > 0) events.permissions_granted = permissions.join(',')
			analyticsEvents.permissionsApproved(events)
		},

		trackLoginSuccess: (method = 'email') => {
			const events = defaultEvents()
			events.login_method = method
			analyticsEvents.userLoginSuccess(events)
		},

		trackLoginFailure: (error) => {
			const events = defaultEvents()
			if (error) events.error_reason = error
			analyticsEvents.userLoginFailure(events)
		},

		trackSurveyStarted: () => {
			const events = defaultEvents()
			const dataStore = useDataStore()
			if (dataStore.surveyId) events.survey_id = dataStore.surveyId
			analyticsEvents.surveyStarted(events)
		},

		trackSurveyCompleted: () => {
			const events = defaultEvents()
			const dataStore = useDataStore()
			if (dataStore.surveyId) events.survey_id = dataStore.surveyId
			if (dataStore.surveyCompletedDate && dataStore.surveyStartedDate) {
				events.survey_duration = Math.round((new Date(dataStore.surveyCompletedDate) - new Date(dataStore.surveyStartedDate)) / 1000) // Duration in seconds
			}
			analyticsEvents.surveyCompleted(events)
		},

		trackTravelDayCompleted: (travelDay) => {
			if (travelDay.journeys && travelDay.journeys.length > 0) {
				const events = defaultEvents()
				const totalTrips = travelDay.journeys.reduce((sum, journey) => sum + (journey.trips ? journey.trips.length : 0), 0)
				events.travel_date = travelDay.date
				events.etl_version = Etl.DEFAULTS.etlVersion
				events.etl_date = Etl.DEFAULTS.etlDate
				events.trips_count_total = totalTrips
				events.journey_count = travelDay.journeys.length
				events.journey_trips_avg = (totalTrips / events.journey_count).toFixed(1)
				analyticsEvents.travelDayCompleted(events)
			}
		},

		trackTravelDay: (travelDay) => {
			if (travelDay.journeys && travelDay.journeys.length > 0) {
				const events = defaultEvents()
				const totalTrips = travelDay.journeys.reduce((sum, journey) => sum + (journey.trips ? journey.trips.length : 0), 0)
				events.travel_date = travelDay.date
				events.etl_version = Etl.DEFAULTS.etlVersion
				events.etl_date = Etl.DEFAULTS.etlDate
				events.journey_count = travelDay.journeys.length
				events.trips_count_total = totalTrips
				events.journey_trips_avg = (totalTrips / events.journey_count).toFixed(1)
				analyticsEvents.trackTravelDay(events)
			}
		},
	}
}
